import { AlertTriangle, RotateCcw } from 'lucide-react'
import { AnalyticsTagSection } from '@/components/builder/AnalyticsTagSection'
import { BaseUrlField } from '@/components/builder/BaseUrlField'
import { CustomTagSection } from '@/components/builder/CustomTagSection'
import { OutputBar } from '@/components/builder/OutputBar'
import { PresetsSection } from '@/components/builder/PresetsSection'
import { UtmSection, type UtmKey, type UtmValues } from '@/components/builder/UtmSection'
import { SubscriptionSwitcher } from '@/components/layout/SubscriptionSwitcher'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import type { Preset, TagDefinitionsState } from '@/hooks/useLinkBuilderDataEntry'
import type { PickerBinding } from '@/lib/pickerBindings'
import type { FetchPickerOptions, PickerOption } from '@/lib/projectOptions'
import type { Subscription } from '@/lib/subscription'
import type { TagEntry } from '@/lib/tagEntry'

interface DrawerPanelProps {
  active: Subscription
  available: Subscription[]
  onPickSubscription: (sub: Subscription) => void
  baseUrl: string
  loadedUrl: string
  utm: UtmValues
  tags: TagEntry[]
  reservedKeys: ReadonlySet<string>
  tagDefinitions: TagDefinitionsState
  pickerBindings: ReadonlyMap<string, PickerBinding>
  fetchOptions: FetchPickerOptions
  focusTagId: string | null
  noToken: boolean
  presets: Preset[]
  activePresetId: string | null
  isModified: boolean
  isBusy: boolean
  outputUrl: string | null
  onBaseUrlChange: (value: string) => void
  onBaseUrlFocus: () => void
  onBaseUrlBlur: () => void
  onLoad: () => void
  onUtmAdd: (key: UtmKey) => void
  onUtmChange: (key: UtmKey, value: string) => void
  onUtmRemove: (key: UtmKey) => void
  onAnalyticsTagAdd: (queryKey: string) => void
  onCustomTagAdd: () => void
  onTagChangeRawKey: (id: string, rawKey: string) => void
  onTagChangeValue: (id: string, value: string) => void
  onTagPick: (id: string, option: PickerOption) => void
  onTagRemove: (id: string) => void
  onResetAll: () => void
  hasAnyParams: boolean
  onApplyPreset: (presetId: string) => void
  onSaveNewPreset: (name: string) => Promise<void>
  onUpdateActivePreset: () => Promise<void>
  onDeleteActivePreset: () => Promise<void>
}

export function DrawerPanel({
  active,
  available,
  onPickSubscription,
  baseUrl,
  loadedUrl,
  utm,
  tags,
  reservedKeys,
  tagDefinitions,
  pickerBindings,
  fetchOptions,
  focusTagId,
  noToken,
  presets,
  activePresetId,
  isModified,
  isBusy,
  outputUrl,
  onBaseUrlChange,
  onBaseUrlFocus,
  onBaseUrlBlur,
  onLoad,
  onUtmAdd,
  onUtmChange,
  onUtmRemove,
  onAnalyticsTagAdd,
  onCustomTagAdd,
  onTagChangeRawKey,
  onTagChangeValue,
  onTagPick,
  onTagRemove,
  onResetAll,
  hasAnyParams,
  onApplyPreset,
  onSaveNewPreset,
  onUpdateActivePreset,
  onDeleteActivePreset,
}: DrawerPanelProps) {
  const showPresets = presets.length > 0 || hasAnyParams

  return (
    <aside className="flex h-dvh w-[420px] shrink-0 flex-col border-r bg-background">
      <header className="flex items-center justify-between gap-2 border-b px-5 py-4">
        <div className="flex items-center gap-2">
          <img src="./favicon.png" alt="" width={20} height={20} className="rounded-sm" />
          <h1 className="text-sm font-medium">N3O Link Builder</h1>
        </div>
        <SubscriptionSwitcher
          current={active}
          available={available}
          onPick={onPickSubscription}
        />
      </header>

      <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
        {noToken && (
          <Alert variant="destructive">
            <AlertTriangle aria-hidden />
            <AlertTitle>Dev token not set</AlertTitle>
            <AlertDescription>
              Tag definitions and presets are unavailable. Set{' '}
              <code className="font-mono">VITE_DEV_BYPASS_TOKEN</code> in{' '}
              <code className="font-mono">.env.local</code> and restart{' '}
              <code className="font-mono">npm run dev</code>.
            </AlertDescription>
          </Alert>
        )}
        {showPresets && (
          <>
            <PresetsSection
              presets={presets}
              activePresetId={activePresetId}
              isModified={isModified}
              isBusy={isBusy}
              hasAnyParams={hasAnyParams}
              onApply={onApplyPreset}
              onSaveNew={onSaveNewPreset}
              onUpdate={onUpdateActivePreset}
              onDelete={onDeleteActivePreset}
            />
            <Separator />
          </>
        )}
        <BaseUrlField
          value={baseUrl}
          loadedValue={loadedUrl}
          onChange={onBaseUrlChange}
          onFocus={onBaseUrlFocus}
          onBlur={onBaseUrlBlur}
          onLoad={onLoad}
        />
        <Separator />
        <UtmSection
          values={utm}
          onAdd={onUtmAdd}
          onChange={onUtmChange}
          onRemove={onUtmRemove}
        />
        <Separator />
        <AnalyticsTagSection
          state={tagDefinitions}
          tags={tags}
          pickerBindings={pickerBindings}
          fetchOptions={fetchOptions}
          focusTagId={focusTagId}
          onAdd={onAnalyticsTagAdd}
          onChangeValue={onTagChangeValue}
          onPick={onTagPick}
          onRemove={onTagRemove}
        />
        <Separator />
        <CustomTagSection
          state={tagDefinitions}
          tags={tags}
          reservedKeys={reservedKeys}
          onAdd={onCustomTagAdd}
          onChangeRawKey={onTagChangeRawKey}
          onChangeValue={onTagChangeValue}
          onRemove={onTagRemove}
        />
        {hasAnyParams && (
          <div className="flex justify-center pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onResetAll}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" aria-hidden />
              Reset all parameters
            </Button>
          </div>
        )}
      </div>

      <OutputBar url={outputUrl} />
    </aside>
  )
}
